import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Modal for visitors to start a 7-day free trial. Captures email/password/name,
 * posts /api/trial/signup, sets the auth cookie, and triggers a context refresh.
 */
export default function TrialSignupModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { refresh } = useAuth();

  useEffect(() => { if (!open) { setDone(false); setBusy(false); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email + password required"); return; }
    setBusy(true);
    try {
      // Read the A/B promo variant the user saw on landing (if any) so the
      // backend can attribute this signup for conversion analytics.
      const promo_variant = (() => {
        try { return window.localStorage.getItem("hot.variant") || undefined; } catch { return undefined; }
      })();
      await api.post("/trial/signup", { email, password, name, promo_variant });
      setDone(true);
      toast.success("Welcome! Your 7-day trial is active.");
      if (refresh) await refresh();
      setTimeout(() => { onClose(); window.location.reload(); }, 1400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
          onClick={onClose}
          data-testid="trial-signup-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full bg-[#0A0A0A] border border-[#007AFF]/40 rounded-sm p-7 relative"
          >
            <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white" data-testid="trial-modal-close">
              <X size={18} />
            </button>
            {done ? (
              <div className="text-center py-8" data-testid="trial-signup-success">
                <CheckCircle size={48} weight="fill" className="text-[#22C55E] mx-auto mb-3" />
                <div className="font-display text-3xl mb-1">YOU&apos;RE IN.</div>
                <div className="text-xs text-zinc-400">Loading your picks…</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#007AFF]">
                  <Sparkle size={14} weight="fill" /> 7-day free trial · no credit card
                </div>
                <h2 className="font-display text-4xl leading-[0.95]">
                  START YOUR<br /><span className="text-[#007AFF]">FREE TRIAL.</span>
                </h2>
                <p className="text-xs text-zinc-400">
                  Full access to today&apos;s picks, tomorrow&apos;s picks, and the live DraftKings moneyline. No credit card required. After 7 days, picks lock until you subscribe.
                </p>
                <div className="space-y-3">
                  <Field label="Name (optional)" value={name} onChange={setName} testid="trial-name" />
                  <Field label="Email" value={email} onChange={setEmail} type="email" testid="trial-email" required />
                  <Field label="Password (6+ chars)" value={password} onChange={setPassword} type="password" testid="trial-password" required />
                </div>
                <button
                  type="submit" disabled={busy}
                  data-testid="trial-submit-btn"
                  className="w-full py-3 bg-[#007AFF] hover:bg-[#0064D6] disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
                >
                  {busy ? "Activating trial…" : "Start 7-Day Trial"}
                </button>
                <p className="text-[10px] text-zinc-600 text-center">
                  Trial gives 7 days of full pick access. By signing up you agree picks are for entertainment only · 21+ · 1-800-GAMBLER.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="w-full mt-1 bg-[#0A0A0A] border border-white/15 rounded-sm px-3 py-2.5 text-sm font-mono-d focus:border-[#007AFF] outline-none transition-colors"
      />
    </div>
  );
}
